import type { PartyRole } from './application';
import type { FieldId } from './ids';
import type { AnswerValue } from './valueTypes';
import { isAnswerPresent } from './valueTypes';

export type FieldValueType = AnswerValue['type'];

export type RuleLiteral = string | number | boolean | null | string[];

export type RuleExpression =
  | {
      op: 'exists';
      fieldId: FieldId;
    }
  | {
      op: 'equals';
      fieldId: FieldId;
      value: RuleLiteral;
    }
  | {
      op: 'one_of';
      fieldId: FieldId;
      values: RuleLiteral[];
    }
  | {
      op: 'and' | 'or';
      rules: RuleExpression[];
    }
  | {
      op: 'not';
      rule: RuleExpression;
    };

export type RequirementRule = boolean | RuleExpression;

export type FieldValidation =
  | {
      type: 'required';
    }
  | {
      type: 'min_length' | 'max_length';
      value: number;
    }
  | {
      type: 'pattern';
      pattern: string;
    }
  | {
      /**
       * Inclusive numeric bounds for `valueType: 'number'` fields. The form
       * engine refuses to validate-clean a value outside the range. Used
       * today for biographic height (feet 1–9, inches 0–11) and weight
       * (50–999 pounds), and for age-derived integer fields.
       */
      type: 'min_value' | 'max_value';
      value: number;
    };

export interface PdfConsumerMetadata {
  formId: string;
  editionDate: string;
  mappingVersion: string;
  required: boolean;
  /**
   * Widget basenames in this PDF that consume this field's value.
   * Empty array allowed for synthetic / cross-consumer-deferred fields.
   * For enum fields with one-of-N widget mappings, use widgetValueProjection
   * instead (or in addition).
   */
  widgetNames?: readonly string[];
  /**
   * Maps a semantic enum value to the PDF widget(s) the value fills in this
   * PDF. Used when a single semantic select maps to one-of-N PDF
   * checkbox/radio widgets (e.g., 27 eligibility-category checkboxes mapping
   * to one eligibility.category.selection enum).
   *
   * The value is either a single widget basename (`string` — the common
   * one-to-one case) or an array of widget basenames (`readonly string[]`)
   * for enum values that fill multiple disambiguating widgets at once. The
   * canonical multi-widget case is the I-864 Part 1 joint-sponsor 1-of-2 /
   * 2-of-2 distinction: both enum values share the primary 1.e checkbox but
   * differ in which sub-radio (P1_Line1e1_Checkbox[0] vs [1]) they fill.
   */
  widgetValueProjection?: Readonly<
    Record<string, string | readonly string[]>
  >;
}

export type DocumentAcceptedFormat = 'pdf' | 'image' | 'doc';

export type DocumentExpectedCount =
  | number
  | { min: number; max?: number };

export interface DocumentSpec {
  /** Human-readable name shown to the user (e.g., "Birth certificate"). */
  displayName: string;
  /** What USCIS needs and why. 1–2 sentences for the UI explainer. */
  description: string;
  /** Acceptable file MIME categories the upload UI should allow. */
  acceptedFormats: DocumentAcceptedFormat[];
  /** Reference into the USCIS form instructions (e.g., "I-485 instructions, Required Initial Evidence"). */
  uscisReference?: string;
  /** For multi-document submissions (e.g., 2 passport-style photos, or "1+ of"). */
  expectedCount?: DocumentExpectedCount;
}

/**
 * Reference to a repeatable field used inside `ComputedSpec.addends`.
 * Means "sum the value of this field across every instance of the
 * group." Singleton field ids in the same array are referenced bare.
 */
export interface RepeatableAddendRef {
  /** The repeatable field whose per-instance value is summed. */
  repeatableField: FieldId;
  /** Matching `repeatableGroupId` on the field above. */
  groupId: string;
}

/**
 * Declares a field whose value is derived from other fields. The
 * renderer displays it as a read-only summary tile (not an input) and
 * writes the computed value back to the engine so persistence and PDF
 * emission see the same sum the UI shows — one source of truth.
 *
 * `op: 'sum'` coerces every addend to a number (absent or non-numeric
 * answers contribute 0). Other ops can be added when needed.
 */
export interface ComputedSpec {
  op: 'sum';
  addends: readonly (FieldId | RepeatableAddendRef)[];
}

export interface FieldDefinition {
  id: FieldId;
  schemaVersion: string;
  sectionId: string;
  partyRole?: PartyRole;
  label: string;
  helpText?: string;
  valueType: FieldValueType;
  required: RequirementRule;
  visibility?: RuleExpression;
  repeatableGroupId?: string;
  validation?: FieldValidation[];
  pdfConsumers: PdfConsumerMetadata[];
  /**
   * Marks the field as derived. The applicant-web renderer treats
   * `computed` fields as read-only summary tiles and writes the result
   * of `op(addends)` back to the engine on every addend change. PDF
   * emission reads the stored value normally; the catalog stays the
   * single source of truth for the relationship.
   */
  computed?: ComputedSpec;
  /**
   * Sidecar metadata for fields whose `valueType` is `document_upload`.
   * Describes the underlying USCIS document requirement so the UI can
   * render an explainer and constrain accepted formats. Omit for all
   * non-document fields.
   */
  documentSpec?: DocumentSpec;
  /**
   * Bounds for fields tagged with a repeatableGroupId.
   * - max: hard upper bound enforced by the form engine
   * - min: floor (e.g., children: min 0; current address: min 1)
   * - overflowStrategy: what to do if user provides more than max instances
   */
  repeatableBound?: {
    min?: number;
    max: number;
    overflowStrategy: 'additional_info' | 'truncate' | 'reject';
  };
  /**
   * Group identifier for "at least one of these fields must have a value"
   * constraint. The form engine enforces ≥1 satisfied per group.
   */
  oneOfGroupId?: string;
}

export interface Answer {
  applicationId: string;
  fieldId: FieldId;
  partyId?: string;
  groupInstanceId?: string;
  value: AnswerValue;
  schemaVersion: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ValidationIssue {
  fieldId: FieldId;
  /**
   * Discriminant for the issue's underlying rule.
   *
   * - `required` — field is required (statically or via rule) and the answer
   *   is absent.
   * - `type_mismatch` — answer's value type does not match the field's
   *   declared `valueType`.
   * - `one_of_group_unsatisfied` — none of the visible members of a
   *   `oneOfGroupId` group has an answer.
   * - `value_out_of_range` — numeric `min_value` / `max_value` violation.
   *   Narrowed to numeric range only as of Phase 0.5c; string-shape rules
   *   now emit the dedicated `string_too_short` / `string_too_long` codes.
   * - `string_too_short` — `min_length` violation on a string-valued answer.
   * - `string_too_long` — `max_length` violation on a string-valued answer.
   * - `pattern_mismatch` — `pattern` regex violation on a string-valued
   *   answer.
   */
  code:
    | 'required'
    | 'type_mismatch'
    | 'one_of_group_unsatisfied'
    | 'value_out_of_range'
    | 'string_too_short'
    | 'string_too_long'
    | 'pattern_mismatch';
  message: string;
  /**
   * For `one_of_group_unsatisfied`, the shared `oneOfGroupId`. Lets the form
   * engine surface a single error per group rather than one per member field.
   */
  oneOfGroupId?: string;
}

export const answerMatchesFieldType = (
  definition: FieldDefinition,
  value: AnswerValue,
): boolean => definition.valueType === value.type;

const literalValueForAnswer = (value?: AnswerValue): RuleLiteral => {
  if (!value) {
    return null;
  }

  switch (value.type) {
    case 'text':
    case 'date':
    case 'select':
    case 'boolean':
    case 'number':
    case 'multi_select':
      return value.value;

    case 'address':
    case 'name':
    case 'document_upload':
      return null;
  }
};

const literalsEqual = (left: RuleLiteral, right: RuleLiteral): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

export const evaluateRuleExpression = (
  rule: RuleExpression,
  answers: readonly Answer[] = [],
): boolean => {
  const answerByFieldId = new Map(
    answers.map(answer => [answer.fieldId, answer.value]),
  );

  switch (rule.op) {
    case 'exists':
      return isAnswerPresent(answerByFieldId.get(rule.fieldId));

    case 'equals':
      return literalsEqual(
        literalValueForAnswer(answerByFieldId.get(rule.fieldId)),
        rule.value,
      );

    case 'one_of': {
      const answerValue = literalValueForAnswer(
        answerByFieldId.get(rule.fieldId),
      );

      return rule.values.some(value => literalsEqual(answerValue, value));
    }

    case 'and':
      return rule.rules.every(childRule =>
        evaluateRuleExpression(childRule, answers),
      );

    case 'or':
      return rule.rules.some(childRule =>
        evaluateRuleExpression(childRule, answers),
      );

    case 'not':
      return !evaluateRuleExpression(rule.rule, answers);
  }
};

export const isFieldRequired = (
  definition: FieldDefinition,
  answers: readonly Answer[] = [],
): boolean =>
  typeof definition.required === 'boolean'
    ? definition.required
    : evaluateRuleExpression(definition.required, answers);

export const validateAnswerValue = (
  definition: FieldDefinition,
  value?: AnswerValue,
  answers: readonly Answer[] = [],
): ValidationIssue[] => {
  if (isFieldRequired(definition, answers) && !isAnswerPresent(value)) {
    return [
      {
        fieldId: definition.id,
        code: 'required',
        message: `${definition.label} is required.`,
      },
    ];
  }

  if (value && !answerMatchesFieldType(definition, value)) {
    return [
      {
        fieldId: definition.id,
        code: 'type_mismatch',
        message: `${definition.label} must be a ${definition.valueType} value.`,
      },
    ];
  }

  // Numeric range checks. Skipped when no value or non-number type — the
  // type_mismatch branch above already catches type errors.
  if (value && value.type === 'number') {
    const numericValue = value.value;
    for (const rule of definition.validation ?? []) {
      if (rule.type === 'min_value' && numericValue < rule.value) {
        return [
          {
            fieldId: definition.id,
            code: 'value_out_of_range',
            message: `${definition.label} must be at least ${rule.value}.`,
          },
        ];
      }
      if (rule.type === 'max_value' && numericValue > rule.value) {
        return [
          {
            fieldId: definition.id,
            code: 'value_out_of_range',
            message: `${definition.label} must be at most ${rule.value}.`,
          },
        ];
      }
    }
  }

  // String-shape checks (min_length / max_length / pattern). Pulled into
  // domain in Phase 0.5c so any consumer of `validateAnswerValue` (PDF
  // engine pre-flight, server-side validators, external integrations) sees
  // the same constraints the form-engine does. USCIS PDF widgets carry hard
  // FieldMaxLength caps; an unvalidated overflow physically cannot fit at
  // PDF write time.
  //
  // Only string-valued answer types participate (`text` / `date` / `select`).
  // Non-string answers fall through silently — the `type_mismatch` branch
  // above is the authoritative report for shape errors. An absent value
  // also falls through: `required` is the authoritative report when the
  // field is required, and an optional empty string is conformant with any
  // length bound (length 0 is not "too short" unless the field is required).
  if (
    value &&
    (value.type === 'text' || value.type === 'date' || value.type === 'select')
  ) {
    const stringValue = value.value;
    const issues: ValidationIssue[] = [];

    for (const rule of definition.validation ?? []) {
      if (rule.type === 'min_length' && stringValue.length < rule.value) {
        issues.push({
          fieldId: definition.id,
          code: 'string_too_short',
          message: `${definition.label} must be at least ${rule.value} characters.`,
        });
      } else if (
        rule.type === 'max_length' &&
        stringValue.length > rule.value
      ) {
        issues.push({
          fieldId: definition.id,
          code: 'string_too_long',
          message: `${definition.label} must be at most ${rule.value} characters.`,
        });
      } else if (rule.type === 'pattern') {
        // Catalog regex strings are authoritative — let them carry their
        // own anchors. Compiled per call; the catalog has ~dozens of
        // pattern rules total, well below any caching threshold.
        if (!new RegExp(rule.pattern).test(stringValue)) {
          issues.push({
            fieldId: definition.id,
            code: 'pattern_mismatch',
            message: `${definition.label} does not match the required pattern.`,
          });
        }
      }
    }

    if (issues.length > 0) return issues;
  }

  return [];
};

/**
 * Validates that every `oneOfGroupId` group in the catalog has at least one
 * member field with a present, visible answer. Returns one issue per
 * unsatisfied group (not one per member field). Members whose `visibility`
 * rule evaluates to false are excluded from the group's evaluation set —
 * the group is only enforced when at least one member is visible.
 *
 * Used today for the sponsor U.S.-citizenship-evidence document slots
 * (`document.sponsor_identity.us_citizen_evidence_*`) — when the sponsor is
 * a U.S. citizen, the form engine refuses to validate-clean unless one
 * evidence document is uploaded.
 */
export const validateOneOfGroups = (
  definitions: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ValidationIssue[] => {
  const answerByFieldId = new Map(
    answers.map(answer => [answer.fieldId, answer.value]),
  );

  const groups = new Map<string, FieldDefinition[]>();
  for (const definition of definitions) {
    if (!definition.oneOfGroupId) continue;
    const list = groups.get(definition.oneOfGroupId) ?? [];
    list.push(definition);
    groups.set(definition.oneOfGroupId, list);
  }

  const issues: ValidationIssue[] = [];
  for (const [groupId, members] of groups) {
    const visibleMembers = members.filter(member =>
      member.visibility
        ? evaluateRuleExpression(member.visibility, answers)
        : true,
    );
    if (visibleMembers.length === 0) continue;

    const satisfied = visibleMembers.some(member =>
      isAnswerPresent(answerByFieldId.get(member.id)),
    );
    if (satisfied) continue;

    const labels = visibleMembers.map(m => m.label).join(', ');
    // Anchor the issue on the alphabetically-first visible member's ID so
    // the choice is stable across catalog reorderings. The `oneOfGroupId`
    // is the canonical pivot for UI grouping; `fieldId` exists to satisfy
    // the ValidationIssue contract.
    const anchor = [...visibleMembers].sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
    )[0];
    issues.push({
      fieldId: anchor.id,
      code: 'one_of_group_unsatisfied',
      message: `At least one of the following must be provided: ${labels}.`,
      oneOfGroupId: groupId,
    });
  }
  return issues;
};
